import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { confirmForgotPassword } from './cognito';
import { JotlyMark } from '../components/JotlyMark';

// Hosted password-reset landing page. The Cognito CustomMessage trigger
// generates an email containing a link of the form:
//   {WEB_APP_ORIGIN}/reset-password?email=foo%40bar.com&code=123456
// We read both off the URL, ask the user for a new password, and call
// ConfirmForgotPassword directly against Cognito (the IDP endpoint is
// CORS-enabled so no backend hop is needed).
//
// Password rules match the SAM password policy: min 8, lowercase + number.
// We don't enforce in JS — Cognito returns InvalidPasswordException with a
// readable message if the user breaks the policy, which we surface verbatim.
export function ResetPasswordScreen() {
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  const code = params.get('code') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const linkValid = !!email && !!code;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPw) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await confirmForgotPassword({ email, code, newPassword });
      setDone(true);
    } catch (err) {
      setError((err as Error).message || 'Could not reset password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <JotlyMark size={32} />
          <span style={{
            font: '700 18px/1 var(--rc-display)',
            letterSpacing: '-0.01em',
            color: 'var(--rc-ink)',
          }}>
            Jotly
          </span>
        </div>
        <h1 style={{
          font: '700 28px/1.1 var(--rc-display)',
          margin: '0 0 24px',
          letterSpacing: '-0.02em',
        }}>
          {done ? 'Password updated.' : 'Set a new password'}
        </h1>

        {!linkValid && (
          <p style={hintStyle}>
            This reset link is incomplete or has expired. Open the Jotly app
            and tap <strong>Forgot password?</strong> to request a new one.
          </p>
        )}

        {linkValid && !done && (
          <>
            <p style={hintStyle}>
              You're resetting the password for{' '}
              <strong style={{ color: 'var(--rc-ink)' }}>{email}</strong>.
              Pick a password with at least 8 characters, one lowercase letter
              and one number.
            </p>
            <form onSubmit={onSubmit} style={formStyle}>
              <label style={labelStyle}>
                <span style={labelTextStyle}>New password</span>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  minLength={8}
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                <span style={labelTextStyle}>Confirm new password</span>
                <input
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  required
                  autoComplete="new-password"
                  minLength={8}
                  style={inputStyle}
                />
              </label>
              <button type="submit" disabled={busy} style={primaryButtonStyle}>
                {busy ? 'Saving…' : 'Set new password'}
              </button>
            </form>
          </>
        )}

        {done && (
          <p style={hintStyle}>
            Open the Jotly app on your phone and sign in with your new
            password. You can close this tab.
          </p>
        )}

        {error && (
          <div role="alert" style={errorStyle}>{error}</div>
        )}
      </div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: 'var(--rc-card)',
  borderRadius: 18,
  padding: '34px 32px 28px',
  boxShadow: '0 12px 36px rgba(20,22,27,0.06), 0 1px 0 rgba(20,22,27,0.03)',
  border: '1px solid var(--rc-border)',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const labelTextStyle: React.CSSProperties = {
  font: '500 12.5px/1 var(--rc-mono)',
  color: 'var(--rc-ink-2)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const inputStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 12,
  border: '1px solid var(--rc-border-2)',
  background: 'var(--rc-card-2)',
  font: '500 15px/1.2 var(--rc-sans)',
  color: 'var(--rc-ink)',
  outline: 'none',
};

const primaryButtonStyle: React.CSSProperties = {
  marginTop: 4,
  appearance: 'none',
  border: 0,
  cursor: 'pointer',
  padding: '12px 16px',
  borderRadius: 12,
  background: 'var(--rc-ink)',
  color: '#fff',
  font: '600 14px/1 var(--rc-sans)',
};

const errorStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 10,
  background: 'var(--rc-danger-soft)',
  color: 'var(--rc-danger)',
  font: '500 13px/1.4 var(--rc-sans)',
};

const hintStyle: React.CSSProperties = {
  margin: '0 0 16px',
  font: '400 14px/1.5 var(--rc-sans)',
  color: 'var(--rc-ink-2)',
};
